import { useCallback, useMemo } from 'react'
import { useGuestParams } from '@/features/account/services/GuestUser/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { useUser, useZipForZipOnlySignedInUser } from '@/features/account/services/User/hooks'
import { catNavCmsQueryOptions, cmsQueryOptions } from '@/features/cms/services/CMS/queryOptions'
import { getAddressParamsForCMS, getCMSParamsWithoutSlug } from '@/features/cms/services/CMS/utils'
import { getStoreParams } from '@/features/shared/utils/dataFetching/storeParamsUtils'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { getUser, getUserZip } from '@/features/account/services/User/utils'
import { getQueryClient } from '@/features/shared/utils/dataFetching/reactQuery/SegwayQueryClient'
import { type GlobalStoreParams } from '@shared/types'
import { noop } from '@/features/shared/utils/noop'

export const useCMSParamsWithoutSlug = () => {
  const user = useUser()
  const zipForZipOnlyUser = useZipForZipOnlySignedInUser()
  const storeParams = useStoreParams()
  const { guestAddress } = useGuestParams() ?? {}

  return useMemo(
    () => getCMSParamsWithoutSlug({ user, zipForZipOnlyUser, storeParams, guestAddress }),
    [guestAddress, storeParams, user, zipForZipOnlyUser],
  )
}

export const usePrefetchCmsShopQueries = () => {
  const queryClient = useQueryClient()

  const prefetchCMSRetailerQueries = useCallback(
    ({
      retailerName,
      ...params
    }: GlobalStoreParams & {
      retailerName?: string
    }) => {
      const user = getUser(getQueryClient())
      if (!user) return

      const storeParams = getStoreParams(user, undefined)
      const addressParams = getAddressParamsForCMS(user, getUserZip(user))

      queryClient
        .query(
          cmsQueryOptions({
            slug: 'retailer_homepage',
            storeParams: { ...storeParams, ...params },
            addressParams,
            subscriptionType: user.subscription?.type ?? undefined,
            hasPlacedMarketplaceOrder: user.has_placed_marketplace_order,
            ...(retailerName && {
              inputVariables: {
                retailer_name: retailerName,
              },
            }),
            enableDeferredHydration: true, // we always want to enabled deferred hydration for RHP
          }),
        )
        .catch(noop)

      queryClient
        .query(
          catNavCmsQueryOptions({
            storeParams: { ...storeParams, ...params },
            addressParams,
            subscriptionType: user.subscription?.type ?? undefined,
            hasPlacedMarketplaceOrder: user.has_placed_marketplace_order,
          }),
        )
        .catch(noop)
    },
    [queryClient],
  )

  return { prefetchCMSRetailerQueries }
}
